import { cn } from "@/core/utils/utils";
import {
  DIFF_KIND,
  GUTTER,
  SIGN,
  TINT,
  WORD,
  type DiffKind,
} from "./diff-marks";
import type { AccessKey } from "./types";
/**
 * Who can read which sheets.
 *
 * This pane replaced a gate rather than joining one. Validation used to refuse
 * any proposal whose access keys moved unless the request had declared it in
 * advance, and no new sheet could satisfy that: a sheet must carry a key, so a
 * new key was refused as an addition and an existing key as a change of
 * assignment. Every way of adding a tab was refused, including the one the
 * owner had asked for in words.
 *
 * So nothing here blocks anything. It shows what the proposal does to reading
 * rights and leaves the decision where it belongs, with the person publishing.
 */
const ROW =
  "flex items-center gap-3 border-b border-stroke-tertiary py-2 pr-3 pl-1";

/**
 * Which way a group moved, in the editor's own diff language.
 *
 * A group covering one more sheet is an addition to what its holders read, so
 * it is green; one covering fewer is red. The key list itself rarely changes,
 * because a new tab normally reuses a key that already exists, so the sheet
 * count is the thing that carries the change.
 */
const KIND: Record<AccessKey["state"], DiffKind> = {
  added: DIFF_KIND.added,
  widened: DIFF_KIND.added,
  removed: DIFF_KIND.removed,
  narrowed: DIFF_KIND.removed,
  // Two-sided, so neither colour alone is honest. It takes the diff's own
  // "changed" mark and leaves the row its ground, the way a changed table does:
  // the sheet names in the row say which way each part went.
  reassigned: DIFF_KIND.changed,
  unchanged: DIFF_KIND.same,
};

/**
 * The sheet count, as a before and after when it moved.
 *
 * The old count struck through beside the new one, the way the written rules
 * show a changed figure, rather than a separate "1 to 2 sheets" column off to
 * the right of the row it describes.
 */
export const AccessRow = ({
  entry,
  change,
  found,
  active,
}: {
  entry: AccessKey;
  change?: number;
  /** Matches the open search. */
  found?: boolean;
  /** The match the reader is on. */
  active?: boolean;
}) => {
  const kind = KIND[entry.state];
  return (
    <div
      data-key={entry.key}
      data-change={change}
      className={cn(
        ROW,
        TINT[kind],
        // The grid's search language: every match takes the same blue, and the
        // one you are on takes the stronger step. One hue at two strengths,
        // because a second hue would say "a different kind of thing" when the
        // truth is "the same thing, you are here".
        { "bg-blue-5": found },
        { "bg-blue-10": active },
      )}
    >
      {/* The gutter the written rules use: the sign only, in the change's own
          ink, running down the document rather than a chip on each row. */}
      <span
        aria-hidden
        className={cn("w-3 shrink-0 text-center", GUTTER[kind])}
      >
        {SIGN[kind] || "\u00a0"}
      </span>
      {entry.state !== "unchanged" && (
        <span className="sr-only">{`${entry.state} by this proposal`}</span>
      )}
      <span className="w-72 shrink-0 truncate">{entry.label}</span>
      <span className="w-20 shrink-0 text-tertiary-text">
        {entry.people === 1 ? "1 person" : `${entry.people} people`}
      </span>
      {/* Which sheets moved, by name, when any did. A reassignment is
          invisible in every other column on this row, so without the names the
          row reads as unchanged. The configuration note keeps the space the
          rest of the time, because it is what matters on a key nothing moved. */}
      {entry.gained_sheets.length || entry.lost_sheets.length ? (
        <span
          className="min-w-0 flex-1 truncate"
          title={[
            ...entry.gained_sheets.map((name) => `+ ${name}`),
            ...entry.lost_sheets.map((name) => `- ${name}`),
          ].join("\n")}
        >
          {/* The two lists are told apart by colour alone on screen, which
              reads aloud as one run of sheet names with no sign of which way
              any of them went. */}
          {!!entry.gained_sheets.length && (
            <span className="sr-only">Added: </span>
          )}
          {entry.gained_sheets.map((name) => (
            <span key={name} className={cn(WORD.added, "mr-1 px-0.5")}>
              {name}
            </span>
          ))}
          {!!entry.lost_sheets.length && (
            <span className="sr-only">Removed: </span>
          )}
          {entry.lost_sheets.map((name) => (
            <span key={name} className={cn(WORD.removed, "mr-1 px-0.5")}>
              {name}
            </span>
          ))}
        </span>
      ) : (
        <span className="min-w-0 flex-1 truncate text-tertiary-text">
          {/* Say what is known. The sheets moved even when the script does not
              name them, and reporting nothing would read as unchanged. */}
          {entry.state === "reassigned"
            ? "Covers different sheets than before"
            : entry.state === "widened"
              ? "Covers more sheets than before"
              : entry.state === "narrowed"
                ? "Covers fewer sheets than before"
                : !entry.configured
                  ? "Not set up yet, so it shows nobody anything"
                  : !entry.active
                    ? "Turned off"
                    : entry.people === 0
                      ? "Nobody holds this yet"
                      : ""}
        </span>
      )}
    </div>
  );
};
